import {
  Container,
  FlexColumnCenterBoth,
  FlexRowCenterBoth,
  Img,
  LoadingSpinner,
  StyleUtils,
} from '@main/core-ui';
import { ASSET_BASE_URL } from '@main/utils';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';

import { BACKEND_API_URL } from '../../constants';
import { detectContentBlocker } from '../../helpers/detectContentBlocker';
import { networkErrorMessages } from './messages';

/**
 * Network error props
 */
export interface NetworkErrorProps {
  /** The src of the network error image */
  imgSrc?: string;
}

/**
 * The URL of the backend server, used in the content blocker error message.
 */
const Url = <code>{BACKEND_API_URL}</code>;

/**
 * Render an error when the backend cannot be reached
 */
export function NetworkError({
  imgSrc = `${ASSET_BASE_URL}/network-error.svg`,
}: NetworkErrorProps): JSX.Element {
  const { formatMessage } = useIntl();
  const [isContentBlockerDetected, setIsContentBlockerDetected] = useState<
    boolean | undefined
  >(undefined);

  useEffect(() => {
    // Detect if a content blocker is active
    const checkForContentBlocker = async (): Promise<void> => {
      const result = await detectContentBlocker();
      setIsContentBlockerDetected(result);
    };

    checkForContentBlocker();
  }, []);

  // Show loading spinner while awaiting content blocker result
  if (isContentBlockerDetected === undefined) {
    return (
      <Container>
        <FlexRowCenterBoth style={{ padding: '5em' }}>
          <LoadingSpinner />
        </FlexRowCenterBoth>
      </Container>
    );
  }

  // Show appropriate error message once detection is complete
  return (
    <Container style={{ maxWidth: '800px' }}>
      <FlexColumnCenterBoth
        style={{ gap: StyleUtils.Spacing.md, textAlign: 'center' }}
      >
        <Helmet>
          <title>{formatMessage(networkErrorMessages.header)}</title>
          <meta
            content={formatMessage(networkErrorMessages.header)}
            name="description"
          />
        </Helmet>
        <h1>{formatMessage(networkErrorMessages.header)}</h1>
        <p>
          {isContentBlockerDetected
            ? formatMessage(networkErrorMessages.contentBlockerMessage, {
                Url,
              })
            : formatMessage(networkErrorMessages.extension)}
        </p>
        <Img
          src={imgSrc}
          width="550px"
          style={{ maxHeight: '404px', maxWidth: '100%' }}
          alt={formatMessage(networkErrorMessages.alt)}
        />
      </FlexColumnCenterBoth>
    </Container>
  );
}

export default NetworkError;
